/**
 * Search Params Builder
 * Maps user preferences and FiltersJson into SearchParams for the browse API
 */

import type { SearchParams } from '../api';
import type { FiltersJson } from '../options';
import { interestedInToGenderIds } from '../options';

export interface UserPrefs {
  age_min?: number;
  age_max?: number;
  distance_max_km?: number;
  latitude?: number | null;
  longitude?: number | null;
  gender_id?: number | null;
  birth_date?: string | null;
  height_cm?: number | null;
  filters?: FiltersJson;
}

/**
 * Apply user preference filters to search params.
 * Mutates the params object.
 */
export function applyPreferenceFilters(params: SearchParams, prefs: UserPrefs): void {
  const filters = prefs.filters;
  const hasIntentEntries = filters?.intent_entries && filters.intent_entries.length > 0;

  // When intent_entries present, they override age/gender/intent - pass them directly
  if (hasIntentEntries) {
    params.intent_entries = filters!.intent_entries;
  } else {
    // Core filters (flat mode)
    if (prefs.age_min !== undefined) params.age_min = prefs.age_min;
    if (prefs.age_max !== undefined) params.age_max = prefs.age_max;

    // Map interested_in strings to gender_ids for search
    if (filters?.interested_in?.length) {
      const mappedIds = interestedInToGenderIds(filters.interested_in);
      if (mappedIds.length > 0) params.gender_ids = mappedIds;
    }
  }

  if (prefs.distance_max_km !== undefined) params.distance_max_km = prefs.distance_max_km;

  // Location for distance filter: travel override > profile location
  if (filters?.travel_lat != null && filters?.travel_lng != null) {
    params.my_lat = filters.travel_lat;
    params.my_lng = filters.travel_lng;
  } else if (prefs.latitude != null && prefs.longitude != null) {
    params.my_lat = prefs.latitude;
    params.my_lng = prefs.longitude;
  }

  // Two-way match and required data
  if (filters?.two_way_match) {
    params.two_way_match = true;
    if (prefs.gender_id != null) params.my_gender_id = prefs.gender_id;
    if (prefs.birth_date) params.my_birth_date = prefs.birth_date;
    if (prefs.height_cm != null) params.my_height_cm = prefs.height_cm;
  }

  // Filter preferences (now flat in filters_json)
  if (!filters) return;

  // Height filter
  if (filters.height_min_cm != null) params.height_min_cm = filters.height_min_cm;
  if (filters.height_max_cm != null) params.height_max_cm = filters.height_max_cm;

  // Lifestyle filters
  if (filters.smoking?.length) params.smoking = filters.smoking;
  if (filters.drinking?.length) params.drinking = filters.drinking;
  if (filters.cannabis?.length) params.cannabis = filters.cannabis;
  if (filters.drugs?.length) params.drugs = filters.drugs;

  // Relationship filters (skip when intent_entries handles it)
  if (!hasIntentEntries && filters.relationship_intents?.length) params.relationship_intents = filters.relationship_intents;
  if (filters.preferred_structure) params.preferred_structure = filters.preferred_structure;

  // Kids filters
  if (filters.has_kids) params.has_kids = filters.has_kids;
  if (filters.wants_more_kids?.length) params.wants_more_kids = filters.wants_more_kids;

  // Other filters
  if (filters.pets_ok_with?.length) params.pets_ok_with = filters.pets_ok_with;
  if (filters.education_levels?.length) params.education_levels = filters.education_levels;
  if (filters.employment_statuses?.length) params.employment_statuses = filters.employment_statuses;
  if (filters.languages?.length) params.languages = filters.languages;
  if (filters.recently_active_hours != null) params.recently_active_hours = filters.recently_active_hours;

  // V1.1 filters
  if (filters.body_types?.length) params.body_types = filters.body_types;
  if (filters.ethnicities?.length) params.ethnicities = filters.ethnicities;
  if (filters.political_views?.length) params.political_views = filters.political_views;
  if (filters.income_ranges?.length) params.income_ranges = filters.income_ranges;
  if (filters.open_to_relocation?.length) params.open_to_relocation = filters.open_to_relocation;
  if (filters.open_to_long_distance?.length) params.open_to_long_distance = filters.open_to_long_distance;
  if (filters.sexual_roles?.length) params.sexual_roles = filters.sexual_roles;
  if (filters.sexual_dynamics?.length) params.sexual_dynamics = filters.sexual_dynamics;

  // V1.2 filters
  if (filters.religions?.length) params.religions = filters.religions;
  if (filters.sexual_orientations?.length) params.sexual_orientations = filters.sexual_orientations;
  if (filters.inclusive_of_trans_nb === true) params.inclusive_of_trans_nb = true;
  if (filters.open_to_enm_poly === true) params.open_to_enm_poly = true;

  // V1.3 filters
  if (filters.zodiac_signs?.length) params.zodiac_signs = filters.zodiac_signs;
  if (filters.exercise_levels?.length) params.exercise_levels = filters.exercise_levels;

  // Kids detail filters
  if (filters.kids_youngest_ages?.length) params.kids_youngest_ages = filters.kids_youngest_ages;
  if (filters.kids_counts?.length) params.kids_counts = filters.kids_counts;
  if (filters.kids_at_home) params.kids_at_home = filters.kids_at_home;

  // User type filters
  if (filters.show_human_users === false) params.show_human_users = false;
  if (filters.show_ai_users === true) params.show_ai_users = true;

  // Activity filters
  if (filters.activity_liked_me === true) params.liked_me = true;
  if (filters.activity_i_liked === true) params.i_liked = true;
  if (filters.activity_i_disliked === true) params.i_disliked = true;
}
